import { bool, node, number, object, oneOfType, string } from "prop-types";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";

import styled from "styled-components";

import CustomScrollbar from "UI/FnrCustomScrollbar/CustomScrollbar";
import { scrollbarInvisibleThin } from "UI/scrollbarStyles";

import useResizeObserver2 from "Utilities/Hooks/useResizeObserver2";

const maxHeightInt = ({ $verticalFill, $maxHeight }) => {
  if ($verticalFill) return "flex-grow: 1;";
  return `max-height: ${$maxHeight};`;
};

const SDiv = styled("div")`
  ${maxHeightInt}
  ${({ $fullWidth }) => ($fullWidth ? "width: 100%;" : "")}
  overflow: auto;
  ${({ $clipX }) => ($clipX ? "overflow-x: clip;" : "")}
  ${scrollbarInvisibleThin}
  scrollbar-width: none;
`;

const CustomScrollbarWrapper = styled("div")`
  ${maxHeightInt}
  ${({ $fullWidth }) => ($fullWidth ? "width: 100%;" : "")}
  overflow: hidden;
`;

const getMaskImage = ({ topFadeHeight, bottomFadeHeight }) => {
  return `linear-gradient(
    to bottom,
    transparent 0,
    black ${topFadeHeight}px,
    black calc(100% - ${bottomFadeHeight}px),
    transparent 100%
    )`;
};

const FadyThing = styled("div")`
  display: flex;
  position: relative;
  ${({ $verticalFill }) => ($verticalFill ? "flex: 1; min-height: 0;" : "")}
`;

// If scrolling appears when it shouldn't, check for fixed-height elements in children
// (e.g., NavLink's RelativeBoundary for FnrNumericIndicator adds height even when empty).
const FnrScrollNFade = forwardRef(
  /**
   *
   * @param {object} param0
   * @param {string} param0.maxHeight defaults to 50vh.
   * @param {number} param0.fadeThickness max thickness of the fade effect in px
   * @param {boolean} param0.scrollbar adds a scrollbar
   * @param {boolean} param0.scrollbarAlwaysShow keeps that scrollbar up while the content overflows rather than revealing it on hover — see CustomScrollbar's `alwaysShow`
   * @param {number} param0.scrollbarContentGap px of clearance between the content and that scrollbar, applied only while it is up — see CustomScrollbar's `contentGap`
   * @param {boolean} param0.verticalFill to be used inside flex container with `min-height: 0` to take up remaining space
   * @param {boolean} param0.fullWidth
   * @param {boolean} param0.clipX
   * @param {import("react").Ref} passedInRef
   * @returns
   */
  (
    {
      maxHeight = "50vh",
      fadeThickness = 20,
      scrollbar,
      scrollbarAlwaysShow,
      scrollbarContentGap,
      verticalFill,
      fullWidth,
      clipX,
      paused,
      children,
      ...rest
    },
    passedInRef
  ) => {
    const localRef = useRef(null);
    const ref = passedInRef || localRef;
    const [maskImage, setMaskImage] = useState("none");

    const updateFade = useCallback(() => {
      if (paused || !ref.current) return;
      const topFadeHeight = Math.min(ref.current.scrollTop / 3, fadeThickness);
      const bottomFadeHeight = Math.min(
        Math.max(
          ref.current.scrollHeight -
            (ref.current.scrollTop + ref.current.clientHeight),
          0
        ) / 3,
        fadeThickness
      );
      setMaskImage(getMaskImage({ topFadeHeight, bottomFadeHeight }));
    }, [paused, fadeThickness, ref]);

    // Update fade on scroll
    useEffect(() => {
      const node = ref.current;
      if (!node) return;
      updateFade();
      node.addEventListener("scroll", updateFade);
      return () => node.removeEventListener("scroll", updateFade);
    });

    // Update fade on container size changes
    useResizeObserver2(ref, updateFade);

    return (
      <FadyThing style={{ maskImage }} $verticalFill={verticalFill} {...rest}>
        {scrollbar ? (
          <CustomScrollbarWrapper
            $maxHeight={maxHeight}
            $verticalFill={verticalFill}
            $fullWidth={fullWidth}
          >
            <CustomScrollbar
              ref={ref}
              colors={typeof scrollbar === "object" ? scrollbar : undefined}
              clipX={clipX}
              alwaysShow={scrollbarAlwaysShow}
              contentGap={scrollbarContentGap}
            >
              {children}
            </CustomScrollbar>
          </CustomScrollbarWrapper>
        ) : (
          <SDiv
            ref={ref}
            $maxHeight={maxHeight}
            $verticalFill={verticalFill}
            $fullWidth={fullWidth}
            $clipX={clipX}
          >
            {children}
          </SDiv>
        )}
      </FadyThing>
    );
  }
);
FnrScrollNFade.propTypes = {
  children: node,
  maxHeight: string,
  fadeThickness: number,
  clipX: bool,
  scrollbar: oneOfType([bool, object]),
  scrollbarAlwaysShow: bool,
  scrollbarContentGap: number,
  verticalFill: bool,
  fullWidth: bool,
};

export default FnrScrollNFade;
